import type {
  CreateGoogleDocConfig,
  CustomGraph,
  CustomNodeType,
  GoogleDocRef,
  GoogleDocTriggerConfig,
  TriggerConfig,
} from '@/types/custom-playbooks';

/**
 * Google Docs in a play: the trigger "A picked Google Doc is updated" and the
 * step "Create a Google Doc". Mirrors cleanse-api `utils/play_google_doc.py`;
 * the server's validator is the authority. A doc or folder is named by its
 * link or id, so both nodes save and edit with no Google connection.
 */

export const MAX_GOOGLE_DOCS = 20;
export const DOC_TITLE_TEMPLATE_LIMIT = 300;
export const DOC_BODY_TEMPLATE_LIMIT = 20_000;
const URL_LIMIT = 2000;

/** The steps a doc record may enter: it has no person or company identity. */
export const DOC_RECORD_STEPS: readonly CustomNodeType[] = [
  'notify',
  'webhook',
  'code',
  'wait',
  'approval',
  'branch',
  'create_google_doc',
];

export const DOC_RECORD_STEP_MESSAGE =
  'A Google Doc record goes only to Notify, Webhook, Code, Wait, Approve, Branch or Create a Google Doc';

const ID = /^[A-Za-z0-9_-]{10,200}$/;
const DOC_PATH =
  /^\/document\/(?:u\/\d+\/)?d\/([A-Za-z0-9_-]{10,200})(?:\/.*)?$/;
const FILE_PATH = /^\/file\/d\/([A-Za-z0-9_-]{10,200})(?:\/.*)?$/;
const FOLDER_PATH =
  /^\/drive\/(?:u\/\d+\/)?(?:mobile\/)?folders\/([A-Za-z0-9_-]{10,200})(?:\/.*)?$/;

function googleLink(text: string): URL | null {
  try {
    const url = new URL(text);
    if (
      url.protocol !== 'https:' ||
      !['docs.google.com', 'drive.google.com'].includes(url.hostname) ||
      url.username ||
      url.password
    )
      return null;
    return url;
  } catch {
    return null;
  }
}

/** The Drive file id a pasted Google Docs link or id names, or null. */
export function docIdOf(ref: unknown): string | null {
  if (typeof ref !== 'string') return null;
  const text = ref.trim();
  if (!text || text.length > URL_LIMIT) return null;
  if (ID.test(text)) return text;
  const url = googleLink(text);
  if (!url) return null;
  if (url.hostname === 'docs.google.com')
    return DOC_PATH.exec(url.pathname)?.[1] ?? null;
  const file = FILE_PATH.exec(url.pathname);
  if (file) return file[1];
  if (url.pathname === '/open' || url.pathname === '/open/') {
    const ids = url.searchParams.getAll('id');
    return ids.length === 1 && ID.test(ids[0]) ? ids[0] : null;
  }
  return null;
}

/** The Drive folder id a pasted folder link or id names, or null. */
export function folderIdOf(ref: unknown): string | null {
  if (typeof ref !== 'string') return null;
  const text = ref.trim();
  if (!text || text.length > URL_LIMIT) return null;
  if (ID.test(text)) return text;
  const url = googleLink(text);
  if (!url || url.hostname !== 'drive.google.com') return null;
  return FOLDER_PATH.exec(url.pathname)?.[1] ?? null;
}

export function googleDocUrl(docId: string): string {
  return `https://docs.google.com/document/d/${docId}/edit`;
}

export function googleFolderUrl(folderId: string): string {
  return `https://drive.google.com/drive/folders/${folderId}`;
}

/** Why a doc trigger's block can't run, in the server's words, or null. */
export function googleDocTriggerIssue(
  block: Partial<GoogleDocTriggerConfig> | undefined
): string | null {
  const docs = block?.documents;
  if (!Array.isArray(docs) || docs.length < 1 || docs.length > MAX_GOOGLE_DOCS)
    return 'Pick or paste 1 to 20 Google Docs';
  const seen = new Set<string>();
  for (const doc of docs) {
    const id = docIdOf(doc?.docId);
    if (!id) return 'Paste a Google Docs link';
    if (seen.has(id)) return 'Pick each doc once';
    seen.add(id);
  }
  if (block?.backfill && !['now', 'historic'].includes(block.backfill))
    return 'Start from now or from the docs as they are';
  return null;
}

/** A trigger config that watches picked Google Docs. */
export function isGoogleDocTrigger(
  config: Partial<TriggerConfig> | undefined
): boolean {
  return config?.source === 'google_doc';
}

/** `docs` with `added` appended, each doc once, at most 20. */
export function withDocs(
  docs: readonly GoogleDocRef[],
  added: readonly GoogleDocRef[]
): GoogleDocRef[] {
  const out = [...docs];
  const ids = new Set(docs.map((doc) => docIdOf(doc.docId) ?? doc.docId));
  for (const doc of added) {
    const id = docIdOf(doc.docId);
    if (!id || ids.has(id) || out.length >= MAX_GOOGLE_DOCS) continue;
    ids.add(id);
    out.push(doc);
  }
  return out;
}

/** Why a Create a Google Doc step can't run yet, or null. */
export function createGoogleDocIssue(
  config: Partial<CreateGoogleDocConfig> | undefined
): string | null {
  const title = config?.title ?? '';
  const body = config?.body ?? '';
  if (!title.trim()) return 'Write the doc title';
  if (title.length > DOC_TITLE_TEMPLATE_LIMIT)
    return `A title is at most ${DOC_TITLE_TEMPLATE_LIMIT} characters`;
  if (!body.trim()) return 'Write the doc body';
  if (body.length > DOC_BODY_TEMPLATE_LIMIT)
    return 'A body is at most 20,000 characters';
  if (config?.folderId && !folderIdOf(config.folderId))
    return 'Paste a Google Drive folder link';
  return null;
}

export function googleDocTriggerSummary(
  block: Partial<GoogleDocTriggerConfig> | undefined
): string {
  const docs = block?.documents ?? [];
  if (!docs.length) return 'Pick Google Docs';
  if (docs.length === 1)
    return `${docs[0].title?.trim() || 'A Google Doc'} is updated`;
  return `One of ${docs.length} Google Docs is updated`;
}

export function createGoogleDocSummary(
  config: Partial<CreateGoogleDocConfig> | undefined
): string {
  const title = config?.title?.trim();
  if (!title) return 'Write the doc title';
  const shown = title.length <= 60 ? title : `${title.slice(0, 59)}…`;
  const folder = config?.folderName?.trim();
  return `Doc: ${shown}${folder ? ` in ${folder}` : ''}`;
}

/** Errors for the steps after a doc trigger that a doc record can't enter. */
export function googleDocRecordIssues(
  graph: CustomGraph
): { nodeId: string; code: string; message: string }[] {
  const trigger = graph.nodes.find((node) => node.type === 'trigger');
  if (!isGoogleDocTrigger(trigger?.config as Partial<TriggerConfig>)) return [];
  return graph.nodes
    .filter(
      (node) => node.type !== 'trigger' && !DOC_RECORD_STEPS.includes(node.type)
    )
    .map((node) => ({
      nodeId: node.id,
      code: 'google_doc_record_step',
      message: DOC_RECORD_STEP_MESSAGE,
    }));
}
